var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Component } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { START_DATE, END_DATE } from 'react-dates/constants';
import { RangePickerController } from '../Calendar';
import Tag from '../Tag';
import { typography } from '../../styles/style-mixins';
const calendarInfoBase = () => css `
  text-align: right;
  margin: 0 23px 0; /* based on react dates */
  padding: 0 0 10px 0;
`;
const CalendarInfo = styled('div')(calendarInfoBase);
const buttonBase = ({ theme, primary }) => css `
  border: none;
  background: none;

  ${typography('two')(theme)};
  margin-left: ${theme.spacings.kilo}};
  cursor: pointer;
  color: ${primary ? 'var(--cui-fg-accent)' : 'var(--cui-fg-normal'};
`;
const InfoButton = styled('span')(buttonBase);
const CalendarWrapper = styled.div `
  margin-top: ${({ theme }) => theme.spacings.byte};
`;
function toDate(date) {
    return date ? date.format('MMM DD') : '';
}
/**
 * Component composed from a <Tag /> and a <RangePickerController /> that has
 * two step process where the user has to click "Apply" to trigger onChange
 */
class CalendarTagTwoStep extends Component {
    constructor() {
        super(...arguments);
        this.state = { startDate: null, endDate: null, focusedInput: null };
        this.tagRef = null; // eslint-disable-line react/sort-comp
        this.handleDatesChange = ({ startDate, endDate }) => {
            this.setState({ startDate, endDate });
            this.handleFocusChange(endDate ? START_DATE : END_DATE);
        };
        this.handleFocusChange = (focusedInput) => this.setState({ focusedInput });
        this.handleClear = () => this.setState({ startDate: null, endDate: null, focusedInput: null });
        this.handleTagClick = (event) => {
            if (this.props.onClick) {
                this.props.onClick(event);
            }
            this.setState(({ focusedInput }) => ({
                focusedInput: focusedInput !== null ? null : START_DATE,
            }));
        };
        this.getDateRangePreview = () => {
            const { startDate, endDate } = this.state;
            if (!startDate && !endDate) {
                return 'Dates';
            }
            return `${toDate(startDate)} - ${toDate(endDate)}`;
        };
        this.handleTagRef = (ref) => {
            this.tagRef = ref;
        };
        this.handleOutsideClick = ({ target }) => {
            if (this.tagRef && !this.tagRef.contains(target)) {
                this.handleFocusChange(null);
            }
        };
        this.handleConfirm = () => {
            const { startDate, endDate } = this.state;
            if (!startDate || !endDate) {
                return;
            }
            this.props.onDatesRangeChange({ startDate, endDate });
            this.handleFocusChange(null);
        };
    }
    render() {
        const _a = this.props, { clearText, confirmText, onDatesRangeChange, tracking } = _a, props = __rest(_a, ["clearText", "confirmText", "onDatesRangeChange", "tracking"]);
        const { focusedInput, startDate, endDate } = this.state;
        const isOpen = focusedInput !== null;
        const isFilled = !!(startDate && endDate);
        return (_jsxs("div", Object.assign({}, props, { children: [_jsx(Tag, Object.assign({ selected: isOpen || isFilled, ref: this.handleTagRef, onClick: this.handleTagClick, tracking: Object.assign({ component: 'calendar-tag-two-step' }, tracking) }, { children: this.getDateRangePreview() })), isOpen && (_jsx(CalendarWrapper, { children: _jsx(RangePickerController, { startDate: startDate, endDate: endDate, onDatesChange: this.handleDatesChange, focusedInput: focusedInput, onOutsideClick: this.handleOutsideClick, numberOfMonths: 2, calendarInfoPosition: "bottom", renderCalendarInfo: () => (_jsxs(CalendarInfo, { children: [_jsx(InfoButton, Object.assign({ onClick: this.handleClear }, { children: clearText })), _jsx(InfoButton, Object.assign({ primary: true, onClick: this.handleConfirm }, { children: confirmText }))] })) }) }))] })));
    }
}
CalendarTagTwoStep.propTypes = {
    /**
     * Callback to receive the set of dates when the user confirms them.
     */
    onDatesRangeChange: PropTypes.func.isRequired,
    /**
     * Text for the clear button
     */
    clearText: PropTypes.string,
    /**
     * Text for the confirm button
     */
    confirmText: PropTypes.string,
    /**
     * @deprecated
     *
     * Use an `onClick` handler to dispatch user interaction events instead.
     */
    tracking: PropTypes.shape({
        label: PropTypes.string.isRequired,
        component: PropTypes.string,
        customParameters: PropTypes.object,
    }),
    /**
     * Function that's called when the date tag is clicked.
     */
    onClick: PropTypes.func,
};
CalendarTagTwoStep.defaultProps = {
    clearText: 'Clear',
    confirmText: 'Apply',
    tracking: {},
};
export default CalendarTagTwoStep;
